import React, { useRef } from "react";
import { SocialAccount, SocialPlatformNames } from "../../../../types/socialTypes";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { socialFormFields } from "../../../../constants/socialConstants";
// Assicurati che il percorso di importazione corrisponda alla posizione del tuo file
import { ButtonWithPopover, ButtonWithPopoverRef } from "../../CommonComponents/ButtonWithPopover";
import CancelIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";

interface SocialFormProps {
    socialPlatform: SocialPlatformNames
    socialAccount: SocialAccount
    onFieldChange: (field: string, value: string) => void
    onDelete: () => void;
    onCreate: () => void;
    onUpdate: () => void;
    mode: 'readonly' | 'updatable'
    onChangeMode: (mode: 'readonly' | 'updatable') => void;
}

const secretString = '**************************';

const SocialAccountForm: React.FC<SocialFormProps> = ({socialAccount, socialPlatform, onFieldChange, onDelete, onCreate, onUpdate, mode, onChangeMode }) => {

    const deletePopoverRef = useRef<ButtonWithPopoverRef>(null);

    // Se non è un vero account ma solo un account in stato di creazione allora è pari a true
    const isNotInstantiated = socialAccount.id === ''


    const handleConfirmDelete = () => {
        onDelete();
        deletePopoverRef.current?.close();
    };

    const handleCancelDelete = () => {
        deletePopoverRef.current?.close();
    };

    // 4. Definiamo il contenuto del popover (testo + bottoni Annulla/Conferma)
    const deletePopoverContent = (
        <Box display="flex" flexDirection="column" gap={1.5} minWidth="200px">
            <Typography variant="body2" color="text.primary">
                Are you sure you want to delete this account?
            </Typography>
            <Box display="flex" justifyContent="center" gap={1}>
                <Button startIcon={<CancelIcon/>} size="small" variant="contained" onClick={handleCancelDelete}>
                    Cancel
                </Button>
                <Button startIcon={<DeleteIcon/>} size="small" variant="contained" onClick={handleConfirmDelete}>
                    Confirm
                </Button>
            </Box>
        </Box>
    );

    return (
        <form>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' }, // Stack verticale su mobile, riga su schermi grandi
                alignItems: 'center',
                gap: 2, // Spazio uniforme tra la sezione input e la sezione bottoni (16px)
                width: '100%'
            }}>
                {/* 1. Contenitore degli Input Field (prende tutto lo spazio disponibile) */}
                <Box sx={{ flexGrow: 1, width: { xs: '100%', lg: 'auto' } }}>
                    <Grid container spacing={2} alignItems="center">
                        {(socialFormFields[socialPlatform] || []).map((socialField) => (
                            <Grid key={socialField.name} size={{ xs: 12 / socialFormFields[socialPlatform].length }}>
                                <TextField
                                    slotProps={{
                                        input: { readOnly: mode === 'readonly' }
                                    }}
                                    value={socialField.secret && mode === 'readonly' ? secretString : (socialAccount[socialField.name as keyof SocialAccount] ? socialAccount[socialField.name as keyof SocialAccount] as string : '' )}
                                    name={socialField.name}
                                    placeholder={socialField.label}
                                    label={socialField.label}
                                    type={'text'}
                                    variant={'outlined'}
                                    onChange={(e) => onFieldChange(socialField.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 2. Contenitore dei Bottoni */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2, // Spazio identico tra Bottone 1 e Bottone 2 (16px)
                    width: { xs: '100%', lg: 'auto' },
                    justifyContent: { xs: 'flex-start', lg: 'flex-start' },
                    mt: { xs: 2, lg: 0 } // Margine solo su mobile quando va a capo
                }}>
                    {
                        isNotInstantiated ?
                            (<Button startIcon={<AddCircleOutlineOutlinedIcon/>} sx={{width: '155.5px'}} variant="contained" onClick={onCreate}>Add Account</Button>) :
                            mode === 'readonly' ?
                                (<Button variant="contained" startIcon={<EditIcon/>} onClick={() => onChangeMode('updatable')}>Update Account</Button>) :
                                (<Button variant="contained" startIcon={<SaveIcon/>} onClick={() => {
                                    onUpdate();
                                    onChangeMode('readonly');
                                }} sx={{ width: '155.5px' }}>Save</Button>)
                    }

                    <ButtonWithPopover
                        ref={deletePopoverRef}
                        variant="contained"
                        startIcon={isNotInstantiated ? <CancelIcon/> : <DeleteIcon/>}
                        popoverContent={isNotInstantiated ? null :  deletePopoverContent}
                        onClick={() => {
                            if(isNotInstantiated)
                                handleConfirmDelete()
                        }}
                        sx={{ width: '155.5px' }}
                    >
                        {isNotInstantiated ? 'Cancel' : 'Delete account'}
                    </ButtonWithPopover>
                </Box>
            </Box>
        </form>
    )
}

export default SocialAccountForm;